import { useState, useRef, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'motion/react';
import type { TreeNode, CrossEdge, ScreenRef } from '../types';
import { canvasSize, flattenTree, NW, NH, topY } from '../layout';
import { useStore } from '../store';
import { NodeEl } from './NodeEl';
import { EdgeLayer } from './EdgeLayer';
import { DragOverlay } from './DragOverlay';
import { FigmaTokenModal } from './FigmaTokenModal';
import { FigmaImportModal } from './FigmaImportModal';
import { LocaleCheckStandaloneModal } from './LocaleCheckStandaloneModal';
import { ScreenCarousel } from './ScreenCarousel';
import { UserFlowView } from './UserFlowView';
import { ExpandedNode } from './ExpandedNode';
import { PreviewPanel } from './PreviewPanel';
import { useDrag } from '../hooks/useDrag';
import { removeNode, addSiblingNode, swapNodes, swapNodeMetadata, findNode } from '../tree';
import type { PickerState, PickerMode } from './EdgePicker';

interface Props {
  allNodes: TreeNode[];
  allEdges: [TreeNode, TreeNode][];
  crossEdges: CrossEdge[];
  doAnim: boolean;
  zoom: number;
  onShowEdgePicker: (toNode: TreeNode, lx: number, ly: number) => void;
  onShowCrossEdgePicker: (ce: CrossEdge, lx: number, ly: number) => void;
  pickerState: PickerState;
  onSetPickerMode: (mode: PickerMode, extra?: Partial<PickerState>) => void;
}

export function Canvas({
  allNodes, allEdges, crossEdges, doAnim, zoom,
  onShowEdgePicker, onShowCrossEdgePicker,
}: Props) {
  const { sel, selNodeId, selTick, setSel, setSelNodeId, drag, getActive, updateActiveTree, clearEdgeAnim, pushUndo, triggerEdgeAnim, figmaTokenOpen, figmaImportOpen, localeCheckOpen, carouselNodeId, setCarouselNodeId } = useStore();
  const cnvRef    = useRef<HTMLDivElement>(null);
  const [editNodeId, setEditNodeId] = useState<string | null>(null);
  const [multiSelIds, setMultiSelIds] = useState<Set<string>>(new Set());
  const [previewStartId, setPreviewStartId] = useState<string | null>(null);
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [userFlowNode, setUserFlowNode] = useState<TreeNode | null>(null);

  type Marquee = { x0: number; y0: number; x1: number; y1: number };
  const [marquee, setMarquee] = useState<Marquee | null>(null);
  const marqueeRef   = useRef<Marquee | null>(null);
  const didMarqueeRef = useRef(false);

  // Toggle a node + all its descendants in multi-select
  const toggleSubtree = useCallback((node: TreeNode) => {
    const ids = flattenTree(node).map(n => n.id);
    setMultiSelIds(prev => {
      const next = new Set(prev);
      if (next.has(node.id)) {
        ids.forEach(id => next.delete(id));
      } else {
        ids.forEach(id => next.add(id));
      }
      return next;
    });
  }, []);

  const clearMultiSel = useCallback(() => setMultiSelIds(new Set()), []);
  const getMultiSel   = useCallback(() => multiSelIds, [multiSelIds]);

  // ── Canvas size ─────────────────────────────────────────────────────────────
  const { cw, ch } = canvasSize(allNodes);

  // ── Marquee selection ───────────────────────────────────────────────────────
  const marqueeMove = useCallback((cx: number, cy: number) => {
    if (!marqueeRef.current) return;
    const rect = cnvRef.current!.getBoundingClientRect();
    const x = (cx - rect.left) / zoom;
    const y = (cy - rect.top)  / zoom;
    const m = { ...marqueeRef.current, x1: x, y1: y };
    marqueeRef.current = m;
    setMarquee({ ...m });
  }, [zoom]);

  const marqueeEnd = useCallback(() => {
    const m = marqueeRef.current;
    if (!m) return;
    marqueeRef.current = null;
    setMarquee(null);
    const minX = Math.min(m.x0, m.x1), maxX = Math.max(m.x0, m.x1);
    const minY = Math.min(m.y0, m.y1), maxY = Math.max(m.y0, m.y1);
    if (maxX - minX < 4 && maxY - minY < 4) return;
    const hit = allNodes.filter(n => {
      const nl = n.x ?? 0, nr = (n.x ?? 0) + NW;
      const nt = topY(n), nb = nt + NH;
      return nr > minX && nl < maxX && nb > minY && nt < maxY;
    });
    if (hit.length) {
      setMultiSelIds(new Set(hit.map(n => n.id)));
      didMarqueeRef.current = true;
    }
  }, [allNodes]);

  // ── Drag ────────────────────────────────────────────────────────────────────
  const handleCommit = useCallback(() => {
    updateActiveTree(getActive().tree);
  }, [getActive, updateActiveTree]);

  const handleAddAndEdit = useCallback((newNode: TreeNode) => {
    requestAnimationFrame(() => setEditNodeId(newNode.id));
  }, []);

  const { dragBegin, dragMove, dragEnd } = useDrag(cnvRef, () => allNodes, handleCommit, handleAddAndEdit, getMultiSel, zoom);

  useEffect(() => {
    const onMove      = (e: MouseEvent)  => { dragMove(e.clientX, e.clientY); marqueeMove(e.clientX, e.clientY); };
    const onUp        = ()               => { dragEnd(); marqueeEnd(); };
    const onTouchMove = (e: TouchEvent)  => { const t = e.touches[0]; dragMove(t.clientX, t.clientY); if (drag.on) e.preventDefault(); };
    const onTouchEnd  = ()               => dragEnd();
    document.addEventListener('mousemove',  onMove);
    document.addEventListener('mouseup',    onUp);
    document.addEventListener('touchmove',  onTouchMove, { passive: false });
    document.addEventListener('touchend',   onTouchEnd);
    return () => {
      document.removeEventListener('mousemove',  onMove);
      document.removeEventListener('mouseup',    onUp);
      document.removeEventListener('touchmove',  onTouchMove);
      document.removeEventListener('touchend',   onTouchEnd);
    };
  }, [dragMove, dragEnd, drag.on, marqueeMove, marqueeEnd]);

  useEffect(() => {
    if (!doAnim) return;
    const maxMs = allEdges.length * 25 + 400;
    const id = window.setTimeout(() => clearEdgeAnim(), maxMs);
    return () => clearTimeout(id);
  }, [doAnim, allEdges.length, clearEdgeAnim]);

  // ── Delete key handler ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const sid = useStore.getState().selNodeId;
      if (!sid) return;
      const flow = getActive();
      const node = allNodes.find(n => n.id === sid);
      if (!node || node.type === 'root' || node.type === 'nav') return;
      const childCount = (node.c ?? []).length;
      const msg = childCount > 0
        ? `Delete "${node.label}" and its ${childCount} child block(s)?`
        : `Delete "${node.label}"?`;
      if (!confirm(msg)) return;
      e.preventDefault();
      pushUndo();
      removeNode(flow.tree, sid);
      setSel(null); setSelNodeId(null);
      triggerEdgeAnim();
      updateActiveTree(flow.tree);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [allNodes, getActive, pushUndo, setSel, setSelNodeId, triggerEdgeAnim, updateActiveTree]);

  // ── Add sibling ─────────────────────────────────────────────────────────────
  const handleAddSibling = useCallback((n: TreeNode) => {
    if (n.type === 'root' || n.type === 'nav') return;
    const flow = getActive();
    pushUndo();
    const newNode = addSiblingNode(flow.tree, n.id);
    triggerEdgeAnim();
    updateActiveTree(flow.tree);
    if (newNode) requestAnimationFrame(() => setEditNodeId(newNode.id));
  }, [getActive, pushUndo, triggerEdgeAnim, updateActiveTree]);

  // ── Swap actions (2-node multi-select) ──────────────────────────────────────
  const handleSwap = useCallback((mode: 'meta' | 'subtree') => {
    if (multiSelIds.size !== 2) return;
    const [aId, bId] = [...multiSelIds];
    const flow = getActive();
    pushUndo();
    if (mode === 'meta') {
      swapNodeMetadata(flow.tree, aId, bId);
    } else {
      const a = findNode(flow.tree, aId);
      const b = findNode(flow.tree, bId);
      if (a && b) swapNodes(flow.tree, a, b);
    }
    triggerEdgeAnim();
    updateActiveTree(flow.tree);
    clearMultiSel();
  }, [multiSelIds, getActive, pushUndo, triggerEdgeAnim, updateActiveTree, clearMultiSel]);

  const handleFigmaLink = useCallback((n: TreeNode, ref: string | null) => {
    pushUndo();
    if (ref === null) {
      delete n.screens;
    } else {
      // Set or replace only the primary (first) screen; preserve import screens beyond index 0
      const rest = (n.screens ?? []).slice(1);
      n.screens = [{ ref, name: n.label, order: 1 }, ...rest];
    }
    updateActiveTree(getActive().tree);
  }, [pushUndo, getActive, updateActiveTree]);

  const handleCarouselOpen = useCallback((n: TreeNode) => {
    setCarouselNodeId(carouselNodeId === n.id ? null : n.id);
  }, [carouselNodeId, setCarouselNodeId]);

  const handleScreenReorder = useCallback((n: TreeNode, screens: ScreenRef[]) => {
    pushUndo();
    n.screens = screens;
    updateActiveTree(getActive().tree);
  }, [pushUndo, getActive, updateActiveTree]);

  const nodeState = useCallback((n: TreeNode) => {
    if (n.id === selNodeId)      return 'act' as const;
    if (!sel)                    return 'def' as const;
    if (!n.b)                    return 'par' as const;
    return n.b === sel ? 'def' as const : 'dim' as const;
  }, [sel, selNodeId]);

  return (
    <>
    <div
      id="cnv"
      ref={cnvRef}
      style={{ width: cw, height: ch, position: 'relative', zoom: zoom }}
      onMouseDown={(e) => {
        if (e.button !== 0) return;
        if ((e.target as Element).closest('[data-nid]')) return;
        const rect = cnvRef.current!.getBoundingClientRect();
        const x = (e.clientX - rect.left) / zoom;
        const y = (e.clientY - rect.top)  / zoom;
        marqueeRef.current = { x0: x, y0: y, x1: x, y1: y };
        setMarquee({ x0: x, y0: y, x1: x, y1: y });
      }}
      onClick={() => {
        if (editNodeId || drag.on) return;
        if (didMarqueeRef.current) { didMarqueeRef.current = false; return; }
        setSel(null); setSelNodeId(null); clearMultiSel();
      }}
    >
      <EdgeLayer
        allNodes={allNodes}
        allEdges={allEdges}
        crossEdges={crossEdges}
        width={cw}
        height={ch}
        doAnim={doAnim}
        sel={sel}
        selNodeId={selNodeId}
        selTick={selTick}
        cnvRef={cnvRef}
        onShowEdgePicker={onShowEdgePicker}
        onShowCrossEdgePicker={onShowCrossEdgePicker}
      />

      {allNodes.map((n) => expandedNodeId === n.id ? null : (
        <NodeEl
          key={n.id}
          node={n}
          state={nodeState(n)}
          multiSel={multiSelIds.has(n.id)}
          onDragBegin={dragBegin}
          onSelect={n => setEditNodeId(n.id)}
          onToggleMulti={() => toggleSubtree(n)}
          onAddSibling={handleAddSibling}
          editNodeId={editNodeId}
          onEditDone={() => setEditNodeId(null)}
          onFigmaLink={handleFigmaLink}
          onCarouselOpen={handleCarouselOpen}
          onLongPress={n => setExpandedNodeId(n.id)}
        />
      ))}

      <DragOverlay width={cw} height={ch} />

      {marquee && (() => {
        const l = Math.min(marquee.x0, marquee.x1);
        const t = Math.min(marquee.y0, marquee.y1);
        const w = Math.abs(marquee.x1 - marquee.x0);
        const h = Math.abs(marquee.y1 - marquee.y0);
        return <div id="marquee" style={{ left: l, top: t, width: w, height: h }} />;
      })()}

      {/* Swap action bar — visible when exactly 2 nodes are multi-selected */}
      {multiSelIds.size === 2 && (
        <div id="swap-bar" onClick={e => e.stopPropagation()}>
          <span id="swap-bar-label">2 selected</span>
          <button className="swap-btn" onClick={() => handleSwap('meta')} title="Swap only node labels — children stay in place">
            Swap nodes
          </button>
          <button className="swap-btn" onClick={() => handleSwap('subtree')} title="Swap entire subtrees including children">
            Swap subtrees
          </button>
          <button className="swap-btn swap-btn-cancel" onClick={clearMultiSel}>✕</button>
        </div>
      )}
    </div>

    {previewStartId && (() => {
      const flow = getActive();
      return (
        <PreviewPanel
          startNodeId={previewStartId}
          nodes={allNodes}
          eventEdges={flow.eventEdges ?? []}
          onClose={() => setPreviewStartId(null)}
        />
      );
    })()}
    {figmaTokenOpen && <FigmaTokenModal />}
    {figmaImportOpen && <FigmaImportModal allNodes={allNodes} />}
    {localeCheckOpen && <LocaleCheckStandaloneModal />}
    {carouselNodeId && (() => {
      const flow = getActive();
      const n = findNode(flow.tree, carouselNodeId);
      if (!n) return null;
      const hasEdges = (flow.eventEdges ?? []).some(e => e.fromNodeId === n.id);
      return (
        <ScreenCarousel
          node={n}
          hasEventEdges={hasEdges}
          onPreview={hasEdges ? () => { setPreviewStartId(n.id); setCarouselNodeId(null); } : undefined}
          onOpenFlow={() => { setUserFlowNode(n); setCarouselNodeId(null); }}
          onClose={() => setCarouselNodeId(null)}
        />
      );
    })()}
    {userFlowNode && (() => {
      const flow = getActive();
      return (
        <UserFlowView
          node={userFlowNode}
          allNodes={allNodes}
          crossEdges={flow.crossEdges ?? []}
          onReorder={screens => handleScreenReorder(userFlowNode, screens)}
          onClose={() => setUserFlowNode(null)}
        />
      );
    })()}
    <AnimatePresence>
      {expandedNodeId && (() => {
        const node = allNodes.find(n => n.id === expandedNodeId);
        return node ? <ExpandedNode key={expandedNodeId} node={node} onClose={() => setExpandedNodeId(null)} /> : null;
      })()}
    </AnimatePresence>
    </>
  );
}
